import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createBlock as _createBlock, resolveComponent as _resolveComponent, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-5c024818"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "row"
}
const _hoisted_2 = { class: "col" }
const _hoisted_3 = {
  key: 1,
  class: "row"
}
const _hoisted_4 = { class: "list-group" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = ["id", "value", "checked", "disabled", "onChange"]
const _hoisted_7 = ["for"]
const _hoisted_8 = {
  key: 0,
  class: "col-md-3"
}
const _hoisted_9 = {
  id: "plant-list-subgroups",
  class: "list-group"
}
const _hoisted_10 = ["onClick"]
const _hoisted_11 = { class: "col-md-7" }
const _hoisted_12 = { class: "row" }
const _hoisted_13 = { class: "col" }
const _hoisted_14 = {
  key: 0,
  class: "row"
}
const _hoisted_15 = {
  class: "card",
  style: {"margin-bottom":"10px"}
}
const _hoisted_16 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h5", { class: "card-header" }, null, -1))
const _hoisted_17 = { class: "card-body" }
const _hoisted_18 = { class: "row" }
const _hoisted_19 = { class: "col-4" }
const _hoisted_20 = { class: "col-8" }
const _hoisted_21 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h6", null, "Possible latin names", -1))
const _hoisted_22 = { class: "card-footer" }
const _hoisted_23 = { class: "float-end" }
const _hoisted_24 = { class: "row" }
const _hoisted_25 = { class: "col" }

import { Group, ListResponse, Paging, Plant, PlantLatinNames, Query, Subgroup } from "@/types/api";
import { computed, inject, onBeforeMount, ref, toRaw, watch } from "vue";
import { useStore } from "vuex";
import eventBus from "@/di/eventBus";
import {
  fillQueryParamsFromQueryString,
  promiseParallel,
  PromiseParallelAction,
  promoteParamsToSearch
} from "@/helpers/tools";
import { plantsApi } from "@/di/api";
import { SCROLL_TO_TOP } from "@/events";
import { plantsService } from "@/di/services";
import AppLoadingMessage from "@/components/app/AppLoadingMessage.vue";
import PlantSunny from "@/components/plant/PlantSunny.vue";
import PlantMinMax from "@/components/plant/PlantMinMax.vue";
import AppImageLightBox from "@/components/app/AppImageLightBox.vue";
import PlantTranslate from "@/components/plant/PlantTranslate.vue";
import AppIcon from "@/components/app/AppIcon.vue";
import AppPagination from "@/components/app/AppPagination.vue";
import { notifyInjectionKey } from "@/keys";
import { useRouter } from "vue-router";

interface Model extends Query {
  filter: {
    group_id: string | null,
    subgroup_id: string | null,
    sunnies: string[] | null,
  },
  paging: Paging
}

interface Lock {
  plantListLoading: boolean,
  initialDataLoading: boolean,
  plantsSpecificLatinNamesLoading: boolean,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PlantList',
  setup(__props) {

const EVENT_PLANT_LIST_LOAD_STARTED = 'plant_list_loading.started'
const EVENT_PLANT_LIST_LOAD_FINISHED = 'plant_list_loading.finished'

const loadingImage = require.context('@/assets/images/plant', false, /\.jpg/)('./leaf.jpg')

const defaultModel = (): Model => ({
  filter: {
    group_id: null,
    subgroup_id: null,
    sunnies: null
  },
  paging: {
    page: 1,
    per_page: 10
  }
})

const store = useStore()
const router = useRouter()

const notify = inject(notifyInjectionKey)

const groups = ref<ListResponse<Group>>({ items: [], total_count: 0 })
const subgroups = ref<ListResponse<Subgroup>>({ items: [], total_count: 0 })
const plants = ref<ListResponse<Plant>>({ items: [], total_count: 0 })
const plantsSpecificLatinNames = ref<ListResponse<PlantLatinNames>>({ items: [], total_count: 0 })
const model = ref<Model>(defaultModel())
const initialLoadingProgress = ref<{ current: number, total: number }>({ current: 0, total: 0 })

const lock = ref<Lock>({ plantListLoading: false, initialDataLoading: false, plantsSpecificLatinNamesLoading: false })

const subgroupsList = computed((): Subgroup[] => subgroups.value.items.filter(({ groups: gs }) => gs.filter(({ id }) => id === model.value.filter.group_id).length > 0))

const isSubgroupsEmpty = computed((): boolean => subgroupsList.value.length === 0)

const isPlantsEmpty = computed((): boolean => plants.value.items.length === 0)

const sunnies = computed((): { code: string, name: string, imageSource: string, checked: boolean }[] => {
  return plantsService.getSunnyInfos()
      .map(({ code, name, imageSource }) => {
        return {
          code,
          name,
          imageSource,
          checked: !!(model.value.filter.sunnies && model.value.filter.sunnies.includes(code))
        }
      })
})

const plantIds = computed((): string[] => plants.value.items.map(({ id }) => id))

const isAuthenticated = computed((): boolean => store.getters['auth/isAuthenticated'])

watch(() => model.value, loadPlantList, { deep: true })

async function loadPlantList (): Promise<void> {
  if (lock.value.plantListLoading) return

  eventBus.$emit(EVENT_PLANT_LIST_LOAD_STARTED)

  lock.value.plantListLoading = true
  lock.value.plantsSpecificLatinNamesLoading = true

  try {
    plants.value = await plantsApi.getPlants(model.value)

    plantsSpecificLatinNames.value = plantIds.value.length > 0
        ? await plantsApi.getPlantSpecificLatinNames(plantIds.value)
        : { items: [], total_count: 0 }

    promoteParamsToSearch(model.value, router)
  } finally {
    lock.value.plantsSpecificLatinNamesLoading = false
    lock.value.plantListLoading = false

    eventBus.$emit(EVENT_PLANT_LIST_LOAD_FINISHED)
  }
}

function onGroupItemClicked (groupId: string): void {
  model.value.filter.group_id = model.value.filter.group_id === groupId ? null : groupId
  model.value.filter.subgroup_id = null
  model.value.paging = defaultModel().paging
}

function onSubgroupItemClicked (subgroupId: string): void {
  model.value.filter.subgroup_id = model.value.filter.subgroup_id === subgroupId ? null : subgroupId
  model.value.paging = defaultModel().paging
}

function onResetClick (): void {
  model.value = defaultModel()
}

function onSunnyCheckboxChanged (checked: boolean, code: string): void {
  let newSunnies = model.value.filter.sunnies || []

  if (checked && !newSunnies.includes(code)) newSunnies.push(code)

  if (!checked && newSunnies.includes(code)) newSunnies = newSunnies.filter((c) => c !== code)

  model.value.filter.sunnies = newSunnies
}

function onPageChanged (paging: Paging): void {
  model.value.paging = paging

  eventBus.$once(EVENT_PLANT_LIST_LOAD_FINISHED, (): void => {
    eventBus.$emit(SCROLL_TO_TOP)
  })
}

function onPlantListLoadingStarted (): void {
  lock.value.plantListLoading = true
}

function onPlaListLoadingFinished (): void {
  lock.value.plantListLoading = false
}

async function onPlantTranslated (): Promise<void> {
  await loadPlantList()

  notify?.({ text: 'Plant translated' })
}

function getPlantSpecificLatinNames (plantId: string): string[] {
  return ((plantsSpecificLatinNames.value.items || []).filter(({ id }) => id === plantId)[0]?.specific_latin_names || [])
}

onBeforeMount((): void => {
  eventBus.$on(EVENT_PLANT_LIST_LOAD_STARTED, onPlantListLoadingStarted)
  eventBus.$on(EVENT_PLANT_LIST_LOAD_FINISHED, onPlaListLoadingFinished)

  model.value = fillQueryParamsFromQueryString(toRaw(model.value))

  const actions = [
    {
      promise: () => plantsApi.getGroups(),
      resolve: (result): void => {
        groups.value = result
      }
    } as PromiseParallelAction<ListResponse<Group>>,
    {
      promise: () => plantsApi.getSubgroups(),
      resolve: (result): void => {
        subgroups.value = result
      }
    } as PromiseParallelAction<ListResponse<Subgroup>>,
    {
      promise: () => loadPlantList()
    } as PromiseParallelAction
  ]

  initialLoadingProgress.value.current = 0
  initialLoadingProgress.value.total = actions.length

  promiseParallel(actions, {
    beforeAll: (): void => {
      lock.value.initialDataLoading = true
    },
    afterEach: (): void => {
      initialLoadingProgress.value.current++
    },
    afterAll: async () => {
      lock.value.initialDataLoading = false
    }
  })
})

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass({'cursor-progress': lock.value.plantListLoading})
  }, [
    (lock.value.initialDataLoading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(AppLoadingMessage, {
              "progress-current": initialLoadingProgress.value.current,
              "progress-total": initialLoadingProgress.value.total
            }, {
              default: _withCtx(() => [
                _createTextVNode(" Plants loading ")
              ]),
              _: 1
            }, 8, ["progress-current", "progress-total"])
          ])
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("div", {
            class: _normalizeClass({ 'col-md-5': _unref(isSubgroupsEmpty), 'col-md-2': !_unref(isSubgroupsEmpty) })
          }, [
            _createElementVNode("ul", _hoisted_4, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groups.value.items, (group) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: group.id,
                  class: _normalizeClass(["list-group-item list-group-item-action cursor-pointer", {active: model.value.filter.group_id === group.id, disabled: lock.value.plantListLoading}]),
                  onClick: ($event: any) => (onGroupItemClicked(group.id))
                }, _toDisplayString(group.name), 11, _hoisted_5))
              }), 128))
            ]),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(sunnies), (sunny, index) => {
              return (_openBlock(), _createElementBlock("div", {
                key: index,
                class: "form-check",
                style: {"margin-top":"10px"}
              }, [
                _createElementVNode("input", {
                  id: `sunny-checkbox-${sunny.code}`,
                  class: "form-check-input",
                  type: "checkbox",
                  style: {"position":"relative","top":"2px"},
                  value: sunny.code,
                  checked: sunny.checked,
                  disabled: lock.value.plantListLoading,
                  onChange: ($event: any) => (onSunnyCheckboxChanged($event.target.checked, sunny.code))
                }, null, 40, _hoisted_6),
                _createElementVNode("label", {
                  class: "form-check-label",
                  for: `sunny-checkbox-${sunny.code}`
                }, [
                  _createVNode(PlantSunny, {
                    sunny: sunny.code,
                    "with-name": true
                  }, null, 8, ["sunny"])
                ], 8, _hoisted_7)
              ]))
            }), 128)),
            _createElementVNode("div", { style: {"margin-top":"10px"} }, [
              _createElementVNode("button", {
                class: "btn btn-sm btn-primary",
                onClick: onResetClick
              }, " Reset filters ")
            ])
          ], 2),
          (!_unref(isSubgroupsEmpty))
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _createElementVNode("ul", _hoisted_9, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(subgroupsList), (subgroup) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: subgroup.id,
                      class: _normalizeClass(["list-group-item list-group-item-action cursor-pointer", {active: model.value.filter.subgroup_id === subgroup.id, disabled: lock.value.plantListLoading}]),
                      onClick: ($event: any) => (onSubgroupItemClicked(subgroup.id))
                    }, _toDisplayString(subgroup.name), 11, _hoisted_10))
                  }), 128))
                ])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("div", _hoisted_13, [
                _createVNode(AppPagination, {
                  "total-count": plants.value.total_count,
                  "neighbours-count": 5,
                  page: model.value.paging.page,
                  "per-page": model.value.paging.per_page,
                  disabled: lock.value.plantListLoading,
                  onPageChanged: onPageChanged
                }, null, 8, ["total-count", "page", "per-page", "disabled"])
              ])
            ]),
            (!_unref(isPlantsEmpty))
              ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(plants.value.items, (plant) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: plant.id,
                      class: "col-12"
                    }, [
                      _createElementVNode("div", _hoisted_15, [
                        _hoisted_16,
                        _createElementVNode("div", _hoisted_17, [
                          _createElementVNode("div", _hoisted_18, [
                            _createElementVNode("div", _hoisted_19, [
                              _createVNode(AppImageLightBox, {
                                images: _unref(plantsService).buildPlantImageUrls(plant),
                                alt: plant.name,
                                "loading-image": _unref(loadingImage)
                              }, null, 8, ["images", "alt", "loading-image"])
                            ]),
                            _createElementVNode("div", _hoisted_20, [
                              _createElementVNode("h6", null, _toDisplayString(plant.name), 1),
                              _createElementVNode("p", null, _toDisplayString(plant.description.substring(0, 200) + '...'), 1),
                              (lock.value.plantsSpecificLatinNamesLoading)
                                ? (_openBlock(), _createBlock(AppLoadingMessage, { key: 0 }))
                                : _createCommentVNode("", true),
                              (getPlantSpecificLatinNames(plant.id).length)
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                    _hoisted_21,
                                    _createElementVNode("ul", null, [
                                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getPlantSpecificLatinNames(plant.id), (specificLatinName, index) => {
                                        return (_openBlock(), _createElementBlock("li", {
                                          key: `plant-${plant.id}-specific-latin-names-${index}`
                                        }, [
                                          _createElementVNode("em", null, _toDisplayString(specificLatinName), 1)
                                        ]))
                                      }), 128))
                                    ])
                                  ], 64))
                                : _createCommentVNode("", true)
                            ])
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_22, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(plant.sunnies, (sunny, idx) => {
                            return (_openBlock(), _createBlock(PlantSunny, {
                              key: idx,
                              sunny: sunny.value
                            }, null, 8, ["sunny"]))
                          }), 128)),
                          (plant.height)
                            ? (_openBlock(), _createBlock(PlantMinMax, {
                                key: 0,
                                name: "height",
                                value: plant.height
                              }, null, 8, ["value"]))
                            : _createCommentVNode("", true),
                          (plant.distance)
                            ? (_openBlock(), _createBlock(PlantMinMax, {
                                key: 1,
                                name: "distance",
                                value: plant.distance,
                                class: "ms-1"
                              }, null, 8, ["value"]))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", _hoisted_23, [
                            (_unref(isAuthenticated))
                              ? (_openBlock(), _createBlock(PlantTranslate, {
                                  key: 0,
                                  id: plant.id,
                                  class: "me-1",
                                  onTranslated: onPlantTranslated
                                }, null, 8, ["id"]))
                              : _createCommentVNode("", true),
                            _createVNode(_component_router_link, {
                              to: {name: 'plant-info', params: {id: plant.id}},
                              class: "btn btn-primary btn-sm",
                              target: "_blank"
                            }, {
                              default: _withCtx(() => [
                                _createVNode(AppIcon, { icon: "eye" })
                              ]),
                              _: 2
                            }, 1032, ["to"])
                          ])
                        ])
                      ])
                    ]))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_24, [
              _createElementVNode("div", _hoisted_25, [
                _createVNode(AppPagination, {
                  "total-count": plants.value.total_count,
                  "neighbours-count": 5,
                  page: model.value.paging.page,
                  "per-page": model.value.paging.per_page,
                  disabled: lock.value.plantListLoading,
                  onPageChanged: onPageChanged
                }, null, 8, ["total-count", "page", "per-page", "disabled"])
              ])
            ])
          ])
        ]))
  ], 2))
}
}

})